import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../api';
import MPOProjectOverview from '../components/materialPO/MPOProjectOverview';
import MPOLanding from '../components/materialPO/MPOLanding';
import MPOReviewQueue from '../components/materialPO/MPOReviewQueue';
import MPOMyOrders from '../components/materialPO/MPOMyOrders';

const MANAGEMENT_ROLES = ['md', 'dmd', 'super_admin'];
// first approval step (workflow step "Cost Control Review", role cost_control), before Management Approval
const COST_CONTROL_ROLES = ['cost_control', 'super_admin'];
// Cost Control's workspaces, as on Enterprise Requisition's Cost Control Queue (/cost-control/<workspace>)
const CC_WORKSPACES = ['load', 'reviewed', 'pending', 'rejected'];

export default function MaterialPOPage({ user }) {
  const navigate = useNavigate();
  const params = useParams();
  const activeTab = params.tab || 'landing';
  const subPath = (params['*'] || '').split('/').filter(Boolean);

  const goToTab = useCallback((id) => {
    api.clearApiCache();
    navigate(`/material-purchase-orders/${id}`);
  }, [navigate]);

  const userRoles = (user?.roles || []).map(r => r.role_name || r.name);
  const canSeeManagement = userRoles.some(r => MANAGEMENT_ROLES.includes(r));
  const canSeeCostControl = userRoles.some(r => COST_CONTROL_ROLES.includes(r));

  const navItems = [
    { id: 'landing',    label: 'PO Landing' },
    { id: 'my-orders',  label: 'My Orders' },
    ...(canSeeCostControl ? [{ id: 'cost-control', label: 'Cost Control Review' }] : []),
    ...(canSeeManagement ? [{ id: 'management', label: 'Management Approval' }] : []),
    { id: 'overview',   label: 'Purchase Orders Registry' },
  ];

  return (
    <div className="fade-in" style={{ padding: '0 0 24px', marginTop: -28 }}>
      {/* Tab pills */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${navItems.length}, 1fr)`, gap: 8, marginBottom: 20, paddingTop: 16 }}>
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => goToTab(item.id)}
            style={{
              padding: '18px 10px',
              background: activeTab === item.id ? '#0b318bff' : 'var(--bg-card)',
              color: activeTab === item.id ? '#fff' : 'var(--text-primary)',
              border: '1px solid black',
              borderRadius: 12,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              boxShadow: activeTab === item.id ? '0 4px 12px rgba(11,49,139,0.2)' : 'var(--shadow-sm)',
              transition: 'all 0.2s ease',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ background: 'var(--bg-card)', border: '3px solid black', borderRadius: 16, minHeight: 400, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
        {activeTab === 'landing' && (
          <MPOLanding onGoToMyOrders={() => goToTab('my-orders')} />
        )}
        {activeTab === 'my-orders' && (
          <MPOMyOrders />
        )}
        {activeTab === 'cost-control' && canSeeCostControl && (
          <MPOReviewQueue
            key="cost-control"
            stage="cost_control"
            // an unknown workspace slug shows the picker, not an empty table
            view={CC_WORKSPACES.includes(subPath[0]) ? subPath[0] : null}
            onViewChange={(v) => navigate(v ? `/material-purchase-orders/cost-control/${v}` : '/material-purchase-orders/cost-control')}
          />
        )}
        {activeTab === 'cost-control' && !canSeeCostControl && (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)', fontSize: 14 }}>
            You do not have permission to view this section.
          </div>
        )}
        {activeTab === 'management' && canSeeManagement && (
          <MPOReviewQueue key="management" stage="management" />
        )}
        {activeTab === 'management' && !canSeeManagement && (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)', fontSize: 14 }}>
            You do not have permission to view this section.
          </div>
        )}
        {activeTab === 'overview' && (
          <MPOProjectOverview
            onSelectProject={() => goToTab('landing')}
          />
        )}
      </div>
    </div>
  );
}
